import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Card, Row, Col, Tabs, Button, Tag, Descriptions, Spin, Modal, Input,
  Space, Divider, Progress, Result,
} from 'antd'
import {
  ArrowLeftOutlined, CheckOutlined, CloseOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import useTask from '../../../hooks/useTask'
import SimpleHeader from '../../../helpers/cards-helper/SimpleHeader'
import { useTranslation } from '../../../hooks/useTranslation'
import Comments from './Comments'
import Documents from './Documents'
import Reports from './Reports'
import { getStatusConfig, getStatusKey, BRAND, BRAND_DIVIDER } from '../constants'

const { TabPane } = Tabs
const { TextArea } = Input

export default function TaskDetail() {
  const { id } = useParams()
  const history = useHistory()
  const { t } = useTranslation()
  const {
    task, loading, actionLoading,
    acceptTask, refuseTask, completeTask, completeTaskPartially, markSeen,
  } = useTask(id)

  const [refuseModalVisible, setRefuseModalVisible] = useState(false)
  const [refuseComment, setRefuseComment] = useState('')

  useEffect(() => {
    if (task && !task.is_seen) {
      markSeen()
    }
  }, [task, markSeen])

  const handleRefuse = useCallback(async () => {
    if (!refuseComment.trim()) return
    await refuseTask(refuseComment)
    setRefuseModalVisible(false)
    setRefuseComment('')
  }, [refuseTask, refuseComment])

  const handleComplete = useCallback(() => {
    Modal.confirm({
      title: t('Konfirmo perfundimin'),
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      content: t('Jeni te sigurt qe deshironi ta perfundoni kete detyre?'),
      okText: t('Po, perfundoje'),
      cancelText: t('Anullo'),
      onOk: completeTask,
    })
  }, [completeTask, t])

  const handleCompletePartially = useCallback(() => {
    Modal.confirm({
      title: t('Perfundim i pjesshem'),
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: t('Detyra do te shenjohet si e perfunduar pjeserisht.'),
      okText: t('Konfirmo'),
      cancelText: t('Anullo'),
      onOk: completeTaskPartially,
    })
  }, [completeTaskPartially, t])

  if (loading) {
    return (
      <Row gutter={24} className="page-content">
        <Col span={24} style={{ textAlign: 'center', paddingTop: 100 }}>
          <Spin size="large" />
        </Col>
      </Row>
    )
  }

  if (!task) {
    return (
      <Row gutter={24} className="page-content">
        <Col span={24}>
          <Result
            status="404"
            title={t('Detyra nuk u gjet')}
            extra={
              <Button type="primary" onClick={() => history.push('/tasks')}>
                {t('Kthehu tek detyrat')}
              </Button>
            }
          />
        </Col>
      </Row>
    )
  }

  const taskData = task.task || task
  const status = getStatusKey(task)
  const statusConfig = getStatusConfig(status)
  const statusLabel = task.task_status?.name || statusConfig.label
  // Map to action states
  const isPending = ['pending', '', 'unfinished'].includes(status)
  const isProcessing = ['processing', 'ongoing'].includes(status)
  const isTerminal = ['completed', 'completed_partially', 'refused'].includes(status)

  return (
    <Row gutter={24} className="page-content">
      <Col span={24}>
        <SimpleHeader title={taskData.name || 'Detajet e detyres'} subtitle="Detyra" />
      </Col>

      <Col span={24} className="mt-2">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push('/tasks')}
          style={{ paddingLeft: 0, color: BRAND }}
        >
          Kthehu tek detyrat
        </Button>
      </Col>

      <Col span={24} className="mt-2">
        <Card>
          <Divider orientation="left" style={{ borderTopColor: BRAND_DIVIDER }} className="m-0">
            <h4 className="m-0" style={{ color: BRAND }}>Informacioni i detyres</h4>
          </Divider>

          <Row gutter={24} style={{ marginTop: 16 }}>
            <Col xs={24} lg={16}>
              <h4 style={{ color: BRAND, marginBottom: 4 }}>{taskData.name}</h4>
              {taskData.description && (
                <p style={{ color: '#666', marginBottom: 0 }}>{taskData.description}</p>
              )}
            </Col>
            <Col xs={24} lg={8} className="text-right">
              <Tag color={statusConfig.color} style={{ fontSize: 14, padding: '4px 16px' }}>
                {statusLabel}
              </Tag>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }} />

          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
            <Descriptions.Item label="Krijuar nga">
              {taskData.create_member
                ? `${taskData.create_member.name || ''} ${taskData.create_member.surname || ''}`.trim()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tipi">
              {task.task?.task_type?.name || taskData.task_type?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Prioriteti">
              {task.task?.task_priority?.name || taskData.task_priority?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Data e fillimit">
              {task.start_date ? moment(task.start_date).format('DD/MM/YYYY HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Afati">
              {(task.end_date || task.due_date)
                ? moment(task.end_date || task.due_date).format('DD/MM/YYYY HH:mm')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Pikat">
              {(taskData.points || task.points_taken)
                ? <Tag color="geekblue">{taskData.points || task.points_taken}</Tag>
                : '-'}
            </Descriptions.Item>
            {task.complete_date && (
              <Descriptions.Item label="Perfunduar me">
                {moment(task.complete_date).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            )}
          </Descriptions>

          {taskData.progress != null && (
            <div style={{ marginTop: 16 }}>
              <span style={{ color: '#888', fontSize: 13 }}>Progresi</span>
              <Progress
                percent={taskData.progress}
                status={isTerminal ? undefined : 'active'}
                strokeColor={BRAND}
              />
            </div>
          )}

          {!isTerminal && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <Space wrap>
                {isPending && (
                  <>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={actionLoading}
                      onClick={acceptTask}
                      style={{ width: 160, height: 37, backgroundColor: BRAND, borderColor: BRAND }}
                      aria-label="Prano detyren"
                    >
                      Prano
                    </Button>
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      loading={actionLoading}
                      onClick={() => setRefuseModalVisible(true)}
                      style={{ width: 160, height: 37 }}
                      aria-label="Refuzo detyren"
                    >
                      Refuzo
                    </Button>
                  </>
                )}
                {isProcessing && (
                  <>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={actionLoading}
                      onClick={handleComplete}
                      style={{ width: 160, height: 37, backgroundColor: BRAND, borderColor: BRAND }}
                      aria-label="Perfundo detyren"
                    >
                      Perfundo
                    </Button>
                    <Button
                      loading={actionLoading}
                      onClick={handleCompletePartially}
                      style={{ width: 180, height: 37 }}
                      aria-label="Perfundo pjeserisht"
                    >
                      Perfundo pjeserisht
                    </Button>
                  </>
                )}
              </Space>
            </>
          )}
        </Card>
      </Col>

      <Col span={24} className="mt-2">
        <Card>
          <Tabs defaultActiveKey="reports">
            <TabPane tab="Raportet" key="reports">
              <Reports membertaskId={id} disabled={isTerminal} />
            </TabPane>
            <TabPane tab="Komentet" key="comments">
              <Comments membertaskId={id} disabled={isTerminal} />
            </TabPane>
            <TabPane tab="Dokumentet" key="documents">
              <Documents membertaskId={id} disabled={isTerminal} />
            </TabPane>
          </Tabs>
        </Card>
      </Col>

      <Modal
        title="Refuzo detyren"
        visible={refuseModalVisible}
        onOk={handleRefuse}
        onCancel={() => setRefuseModalVisible(false)}
        okText="Refuzo"
        cancelText="Anullo"
        okButtonProps={{ danger: true, disabled: !refuseComment.trim(), loading: actionLoading }}
        centered
        destroyOnClose
      >
        <TextArea
          rows={4}
          placeholder="Shkruani arsyen e refuzimit..."
          value={refuseComment}
          onChange={(e) => setRefuseComment(e.target.value)}
          aria-label="Arsyeja e refuzimit"
        />
      </Modal>
    </Row>
  )
}
